'use client'

import {
  ClickAwayListener,
  FormControl,
  Grow,
  InputLabel,
  OutlinedInput,
  Paper,
  Popper,
  Stack,
} from '@mui/material'
import {
  useState,
  type ChangeEvent,
  useRef,
  useEffect,
  type SyntheticEvent,
} from 'react'
import { type Minmax } from '@/lib/generated/graphql'
import { Button } from '@/components/library/button'
import { usePaginationContext } from '../utility/pagination/pagination-context'
import { useRouter } from 'next/navigation'
import { useGetRedirectPath } from '@/components/utility/product-filters/product-filters'

interface IMinMaxFilterProps {
  title?: string
  minName?: string
  maxName?: string
  minVal?: number
  maxVal?: number
  onFilterSelect?: (filter: Minmax) => void
}

export const MinMaxFilterSmall = (props: IMinMaxFilterProps) => {
  const router = useRouter()
  const { getRedirectPath } = useGetRedirectPath()
  const { setPage } = usePaginationContext()
  const [minVal, setMinVal] = useState(props.minVal ?? 0)
  const [maxVal, setMaxVal] = useState(props.maxVal ?? 0)

  const handleMinChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | KeyboardEvent,
  ) => {
    const onlyNums = Number.parseInt(
      (event.target as HTMLInputElement).value?.replaceAll(/\D/g, ''),
    )
    setMinVal(Number.isNaN(onlyNums) ? 0 : onlyNums)
  }

  const handlePriceFilterChange = (minMaxValues: Minmax) => {
    const redirectPath = getRedirectPath(
      [
        minMaxValues?.min?.toString() ?? '0',
        minMaxValues?.max?.toString() ?? '0',
      ],
      'price',
    )
    setPage(1)
    router.push(redirectPath, {
      scroll: false,
    })
  }

  const handleMinKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleMinChange(event)
    }
  }

  const handleMaxChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | KeyboardEvent,
  ) => {
    const onlyNums = Number.parseInt(
      (event.target as HTMLInputElement).value?.replaceAll(/\D/g, ''),
    )
    setMaxVal(Number.isNaN(onlyNums) ? 0 : onlyNums)
  }

  const handleMaxKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleMaxChange(event)
    }
  }

  const handleMinBlur = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newVal = Number.parseInt(event.target.value) || 0
    handlePriceFilterChange({
      min: newVal,
      max: maxVal,
    })
  }

  const handleMaxBlur = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newVal = Number.parseInt(event.target.value) || 0
    handlePriceFilterChange({
      min: minVal,
      max: newVal,
    })
  }

  return (
    <Stack>
      <div className='p-2 text-center text-base text-text-secondary'>
        <FormControl sx={{ m: 1, width: '100%' }}>
          <InputLabel id='min-name' className='!text-primary'>
            {props.minName}
          </InputLabel>
          <OutlinedInput
            id='min-name'
            value={minVal ?? 0}
            fullWidth
            startAdornment={<div className='text-primary'>$</div>}
            label={props.minName}
            onChange={handleMinChange}
            onBlur={handleMinBlur}
            onKeyDown={() => handleMinKeyDown}
            className='h-[56px]'
            sx={{
              '& .MuiInputBase-input': {
                boxShadow: 'none !important',
              },
              '&:hover > .MuiOutlinedInput-notchedOutline': {
                borderColor: '#713fff',
              },
            }}
          />
        </FormControl>
      </div>
      <div className='p-2 text-center text-base text-text-secondary'>
        <FormControl sx={{ m: 1, width: '100%' }}>
          <InputLabel id='max-name' className='!text-primary'>
            {props.maxName}
          </InputLabel>
          <OutlinedInput
            id='max-name'
            value={maxVal ?? 0}
            fullWidth
            label={props.maxName}
            startAdornment={<div className='text-primary'>$</div>}
            onChange={handleMaxChange}
            onBlur={handleMaxBlur}
            onKeyDown={() => handleMaxKeyDown}
            className='h-[56px]'
            sx={{
              '& .MuiInputBase-input': {
                boxShadow: 'none !important',
              },
              '&:hover > .MuiOutlinedInput-notchedOutline': {
                borderColor: '#713fff',
              },
            }}
          />
        </FormControl>
      </div>
    </Stack>
  )
}

export const MinMaxFilter = (props: IMinMaxFilterProps) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }
  const handleClose = (event: Event | SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return
    }
    setOpen(false)
  }
  const prevOpen = useRef(open)

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current?.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <div>
      <Button
        ref={anchorRef}
        id='min-max-filter-button'
        onClick={handleToggle}
        data-testid='filter-title'
        className='ml-0 min-w-16 text-primary-dark no-underline'
      >
        {props?.title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        style={{ position: 'relative', zIndex: 999 }}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div style={{ width: 'max-content', marginRight: 8 }}>
                  <MinMaxFilterSmall {...props} />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}
